import React, { useState, useEffect } from "react";
import { useUserAuth } from "../context/UserAuthContext";
import { db } from "../../config/firebase";
import { addDoc, collection, query, where, getDocs, orderBy } from "firebase/firestore";
import { useQuery } from "@tanstack/react-query";
import { PiImageSquareDuotone, PiSpinnerBold, PiCloudArrowUp } from "react-icons/pi";
import { IoChevronBack, IoChevronForward, IoClose, IoDownloadOutline } from "react-icons/io5";
import { uploadFileToCloudinary } from "../../config/cloudinary";
import { getCurrentTimeInUTCFormat } from "../../common/methods";
import toast from "react-hot-toast";

const ImageEditor = () => {
  const { user } = useUserAuth();
  const [selectedImage, setSelectedImage] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  
  // Store filter states for each image ID
  const [imageFilters, setImageFilters] = useState({});

  const defaultFilters = {
    brightness: 100,
    contrast: 100,
    saturate: 100,
    grayscale: 0,
    sepia: 0,
    hueRotate: 0,
    blur: 0,
  };

  // Get current filters for selected image or defaults
  const filters = selectedImage ? (imageFilters[selectedImage.id] || defaultFilters) : defaultFilters;

  // Fetch only images from Firebase
  const { data: images = [], isLoading } = useQuery({
    queryKey: ["editor-images", user?.email],
    queryFn: async () => {
      if (!user || !user.email) return [];
      const q = query(
        collection(db, "files"),
        where("userEmail", "==", user.email),
        orderBy("timeCreated", "desc")
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs
        .map(doc => ({ ...doc.data(), id: doc.id }))
        .filter(file => file.contentType?.startsWith("image/"));
    },
    enabled: !!user?.email,
  });

  const handleFilterChange = (filter, value) => {
    if (!selectedImage) return;
    setImageFilters(prev => ({
      ...prev,
      [selectedImage.id]: {
        ...(prev[selectedImage.id] || defaultFilters),
        [filter]: value
      }
    }));
  };

  const resetFilters = () => {
    if (!selectedImage) return;
    setImageFilters(prev => ({
      ...prev,
      [selectedImage.id]: defaultFilters
    }));
  };

  const filterStyle = {
    filter: `
      brightness(${filters.brightness}%)
      contrast(${filters.contrast}%)
      saturate(${filters.saturate}%)
      grayscale(${filters.grayscale}%)
      sepia(${filters.sepia}%)
      hue-rotate(${filters.hueRotate}deg)
      blur(${filters.blur}px)
    `,
  };

  const MAX_CLOUDINARY_FILE_SIZE = 10485760;

  const createBlob = (canvas, type, quality) =>
    new Promise(resolve => canvas.toBlob(resolve, type, quality));

  const getScaledCanvas = (canvas, scale) => {
    if (scale === 1) return canvas;
    const scaled = document.createElement("canvas");
    scaled.width = Math.round(canvas.width * scale);
    scaled.height = Math.round(canvas.height * scale);
    const ctx = scaled.getContext("2d");
    ctx.drawImage(canvas, 0, 0, scaled.width, scaled.height);
    return scaled;
  };

  const getOptimizedUploadBlob = async (canvas, originalType) => {
    const outputType = originalType === "image/png" ? "image/webp" : originalType;
    const strategies = [
      { type: outputType, quality: 0.9, scale: 1 },
      { type: outputType, quality: 0.8, scale: 1 },
      { type: outputType, quality: 0.7, scale: 0.9 },
      { type: outputType, quality: 0.65, scale: 0.75 },
      { type: outputType, quality: 0.55, scale: 0.6 },
    ];

    for (const { type, quality, scale } of strategies) {
      const candidateCanvas = getScaledCanvas(canvas, scale);
      const blob = await createBlob(candidateCanvas, type, quality);
      if (!blob) continue;
      if (blob.size <= MAX_CLOUDINARY_FILE_SIZE) {
        return blob;
      }
    }

    throw new Error("Edited image is too large to upload after compression.");
  };

  const downloadImage = () => {
    const canvas = document.createElement("canvas");
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.src = selectedImage.url;
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d");
      ctx.filter = filterStyle.filter;
      ctx.drawImage(img, 0, 0, img.width, img.height);
      const link = document.createElement("a");
      link.download = `edited-${selectedImage.name}`;
      link.href = canvas.toDataURL(selectedImage.contentType || "image/png");
      link.click();
    };
  };

  const uploadEditedImage = async () => {
    if (!selectedImage) return;
    setIsUploading(true);
    const toastId = toast.loading("Uploading edited image...");
    
    try {
      const canvas = document.createElement("canvas");
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.src = selectedImage.url;
      
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
      });

      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d");
      ctx.filter = filterStyle.filter;
      ctx.drawImage(img, 0, 0, img.width, img.height);
      
      const optimizedBlob = await getOptimizedUploadBlob(canvas, selectedImage.contentType || "image/png");
      const extension = optimizedBlob.type.split("/")[1] || "png";
      const fileName = `edited-${selectedImage.name.replace(/\.[^/.]+$/, "")}.${extension}`;
      const file = new File([optimizedBlob], fileName, { type: optimizedBlob.type });
      
      const uploadResult = await uploadFileToCloudinary(file);
      
      const fileData = {
        name: fileName,
        url: uploadResult.secure_url,
        size: optimizedBlob.size,
        contentType: optimizedBlob.type,
        userEmail: user.email,
        timeCreated: getCurrentTimeInUTCFormat(),
        publicId: uploadResult.public_id,
      };

      await addDoc(collection(db, "files"), fileData);
      
      toast.success("Image re-uploaded successfully!", { id: toastId });
      setSelectedImage(null);
    } catch (error) {
      console.error("Error uploading edited image:", error);
      toast.error(error.message?.includes("too large") ? "Edited image is too large to upload. Try a smaller image." : "Failed to upload edited image", { id: toastId });
    } finally {
      setIsUploading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-100px)]">
        <PiSpinnerBold className="animate-spin text-4xl text-blue-500" />
      </div>
    );
  }

  return (
    <div className="p-6 h-[calc(100vh-100px)] overflow-hidden flex flex-col">
      <div className="flex flex-col gap-4 md:flex-row md:justify-between md:items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <PiImageSquareDuotone className="text-blue-500" /> Image Editor
          </h2>
          <p className="text-sm text-gray-500 mt-1">Select an image to refine it with live preview and controls.</p>
        </div>

        {selectedImage && (
          <div className="flex flex-wrap gap-2 justify-start md:justify-end">
            <button
              onClick={uploadEditedImage}
              disabled={isUploading}
              className="bg-green-600 hover:bg-green-700 text-white p-2 px-4 rounded-lg shadow-md transition-colors flex items-center gap-2 font-medium disabled:bg-green-400 disabled:cursor-not-allowed"
            >
              {isUploading ? <PiSpinnerBold className="animate-spin text-xl" /> : <PiCloudArrowUp className="text-xl" />} Re-upload
            </button>
            <button
              onClick={downloadImage}
              className="bg-blue-500 hover:bg-blue-600 text-white p-2 px-4 rounded-lg shadow-md transition-colors flex items-center gap-2 font-medium"
            >
              <IoDownloadOutline className="text-xl" /> Download
            </button>
            <button
              onClick={resetFilters}
              className="bg-white/90 hover:bg-white p-2 px-4 rounded-lg shadow-md transition-colors font-medium border border-gray-200"
            >
              Reset
            </button>
          </div>
        )}
      </div>

      {!selectedImage ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-5 overflow-y-auto pr-2">
          {images.length > 0 ? (
            images.map((image) => (
              <div
                key={image.id}
                onClick={() => setSelectedImage(image)}
                className="group relative rounded-3xl overflow-hidden cursor-pointer border border-transparent hover:border-blue-400 transition-all shadow-lg bg-white"
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src={image.url}
                    alt={image.name}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                </div>
                <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <span className="text-white text-sm font-semibold">Edit Image</span>
                </div>
                <div className="p-4 bg-white">
                  <p className="text-sm font-semibold text-gray-800 truncate">{image.name}</p>
                  <p className="text-xs text-gray-500 mt-1">Tap to open the editor.</p>
                </div>
              </div>
            ))
          ) : (
            <div className="col-span-full flex flex-col items-center justify-center py-20 bg-white rounded-3xl border-2 border-dashed border-gray-200">
              <PiImageSquareDuotone className="text-6xl text-gray-300 mb-4" />
              <p className="text-gray-500 font-medium">No images found in your space</p>
              <p className="text-gray-400 text-sm">Upload images in My Space to edit them here</p>
            </div>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[1.6fr_1fr] gap-6 flex-1 pb-4 overflow-hidden min-h-0">
          <div className="bg-gradient-to-br from-slate-100 via-white to-slate-100 rounded-[32px] shadow overflow-hidden flex flex-col min-h-0">
            <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-slate-200">
              <button
                onClick={() => setSelectedImage(null)}
                className="bg-white shadow-sm hover:bg-slate-50 p-2 rounded-full transition"
                title="Back to gallery"
              >
                <IoChevronBack className="text-lg text-slate-700" />
              </button>
              <div className="flex-1 text-center">                
                <h3 className="text-md font-semibold text-slate-800">{selectedImage.name}</h3>
              </div>
            </div>

            <div className="flex-1 flex items-center justify-center p-8 overflow-hidden">
              <img
                src={selectedImage.url}
                alt="Editing"
                style={filterStyle}
                className="max-w-full max-h-full object-contain rounded-3xl shadow-2xl transition-all duration-300"
              />
            </div>
          </div>

          <div className="w-full bg-white rounded-[32px] shadow-sm border overflow-hidden min-h-0 flex flex-col">
            <div className="sticky top-0 z-10 bg-white border-b border-slate-200 px-6 py-5">
              <p className="text-xs uppercase tracking-[0.2em] text-slate-500">Filters & adjustments</p>              
            </div>

            <div className="p-6 overflow-y-auto min-h-0 flex-1">
              <div className="space-y-6">
              {[
                { label: "Brightness", key: "brightness", min: 0, max: 200, unit: "%" },
                { label: "Contrast", key: "contrast", min: 0, max: 200, unit: "%" },
                { label: "Saturation", key: "saturate", min: 0, max: 200, unit: "%" },
                { label: "Grayscale", key: "grayscale", min: 0, max: 100, unit: "%" },
                { label: "Sepia", key: "sepia", min: 0, max: 100, unit: "%" },
                { label: "Hue Rotate", key: "hueRotate", min: 0, max: 360, unit: "deg" },
                { label: "Blur", key: "blur", min: 0, max: 20, unit: "px" },
              ].map((item) => (
                <div key={item.key} className="space-y-2">
                  <div className="flex justify-between">
                    <label className="text-sm font-medium text-gray-700">{item.label}</label>
                    <span className="text-xs text-blue-500 font-bold">{filters[item.key]}{item.unit}</span>
                  </div>
                  <input
                    type="range"
                    min={item.min}
                    max={item.max}
                    value={filters[item.key]}
                    onChange={(e) => handleFilterChange(item.key, parseInt(e.target.value))}
                    className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-500"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      )}
    </div>
  );
};

export default ImageEditor;
